
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '@/components/shared/SEO';
import CountryHeroSection from '@/components/country/CountryHeroSection';
import CountrySearchSection from '@/components/country/CountrySearchSection';
import CountryInfoBanner from '@/components/country/CountryInfoBanner';
import CitiesList from '@/components/country/CitiesList';
import BreadcrumbSchema from '@/components/seo/components/BreadcrumbSchema';
import CountryConversionSection from '@/components/country/CountryConversionSection';
import CountryFAQSection from '@/components/country/CountryFAQSection';
import { getCountryBySlug } from '@/data/countries/index';
import NotFound from './NotFound';

const CountryPsychologists = () => {
  const { countrySlug } = useParams<{ countrySlug: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const citiesPerPage = 100; // Maximum 100 cities per page

  if (!countrySlug) {
    return <NotFound />;
  }

  const country = getCountryBySlug(countrySlug);

  if (!country) {
    return <NotFound />;
  }

  console.log(`Country: ${country.name}, Total cities: ${country.topCities.length}`);

  const filteredCities = country.topCities.filter(city =>
    city.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log(`Filtered cities: ${filteredCities.length}, Search term: "${searchTerm}"`);

  const totalPages = Math.ceil(filteredCities.length / citiesPerPage);
  const startIndex = (currentPage - 1) * citiesPerPage;
  const currentCities = filteredCities.slice(startIndex, startIndex + citiesPerPage);

  console.log(`Current page: ${currentPage}, Total pages: ${totalPages}, Current cities count: ${currentCities.length}`);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEO
        title={`Best Psychologists in ${country.name} | Licensed Mental Health Professionals`}
        description={`Find qualified psychologists and therapists in ${country.name}. Browse our directory of ${filteredCities.length}+ cities with licensed mental health professionals. Book online or in-person therapy sessions.`}
        keywords={`psychologists in ${country.name}, therapists ${country.name}, mental health professionals ${country.name}, psychology services ${country.name}, online therapy ${country.name}, licensed therapists ${country.name}`}
        canonicalUrl={`https://psychotherapist.help/psychologists/${country.slug}`}
      />
      
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://psychotherapist.help' },
          { name: 'Countries', url: 'https://psychotherapist.help/countries' },
          { name: `Psychologists in ${country.name}`, url: `https://psychotherapist.help/psychologists/${country.slug}` }
        ]}
      />
      
      <CountryHeroSection country={country} />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <CountrySearchSection
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            countryName={country.name}
            startIndex={startIndex}
            endIndex={Math.min(startIndex + citiesPerPage, filteredCities.length)}
            totalCities={filteredCities.length}
            currentPage={currentPage}
            totalPages={totalPages}
          />

          <CountryInfoBanner
            countrySlug={country.slug}
            currentPage={currentPage}
            searchTerm={searchTerm}
            citiesCount={filteredCities.length}
          />

          <CitiesList
            cities={currentCities}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            countryName={country.name}
          />
        </div>
      </div>
      
      <CountryConversionSection
        countryName={country.name}
        totalCities={filteredCities.length}
        countrySlug={country.slug}
      />
      
      <CountryFAQSection countryName={country.name} />
    </>
  );
};

export default CountryPsychologists;
